import { useSearch } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useAdminAuth } from '../../shared/auth/admin-auth-context'
import { NO_NAME, userTitle } from '../users/identity'
import { ChatPane } from './chat-pane'
import { FilterTabs } from './filter-tabs'
import { defaultSort } from './labels'
import type { QueueFilter, SortDir } from './labels'
import { QueueList } from './queue-list'
import { useQueue } from './queries'
import styles from './readings.module.css'

const EMPTY: Readonly<Record<QueueFilter, string>> = {
  new: 'Новых вопросов нет. Когда человек закажет разбор или напишет после ответа, он появится здесь.',
  in_progress: 'В работе ничего нет. Возьмите вопрос из «Ждут ответа».',
  done: 'Готовых разборов пока нет.',
  rejected: 'Отклонённых вопросов нет.',
  all: 'Разборов пока нет.',
}

export function ArcanaReadingsPage() {
  const { user, status } = useAdminAuth()
  const allowed = status === 'authenticated' && (user?.role === 'expert' || user?.role === 'admin')
  const [filter, setFilter] = useState<QueueFilter>('new')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortDir | null>(null)
  const { order } = useSearch({ strict: false })
  const [selectedId, setSelectedId] = useState<string | null>(typeof order === 'string' ? order : null)
  const [opened, setOpened] = useState(typeof order === 'string')
  const queue = useQueue(filter, allowed)
  const dir = sort ?? defaultSort(filter)

  const items = useMemo(() => {
    const q = search.trim().toLowerCase()
    const list = (queue.data?.items ?? []).filter((i) => {
      if (!q) return true
      const name = i.person ? userTitle(i.person).title : NO_NAME
      return `${name} ${i.topic_title} ${i.question ?? ''}`.toLowerCase().includes(q)
    })
    return list.sort((a, b) => {
      const d = new Date(a.last_message_at).getTime() - new Date(b.last_message_at).getTime()
      return dir === 'old' ? d : -d
    })
  }, [queue.data, search, dir])

  if (!allowed) return <p className="error-box">Раздел доступен только Людмиле и администратору.</p>

  const selected = items.find((i) => i.id === selectedId) ?? items[0] ?? null

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1>Разборы</h1>
        <p className="muted">Переписка с человеком. Голосовой разбор сразу появится у него в чате, бот пришлёт уведомление.</p>
      </header>

      <div className={styles.layout} data-opened={opened && selected ? 'true' : undefined}>
        <section className={styles.listPane} aria-label="Список разборов">
          <div className={styles.listHead}>
            <label className={styles.search}>
              <input
                type="search"
                value={search}
                placeholder="Имя, тема или вопрос"
                aria-label="Поиск по разборам"
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                type="button"
                className={styles.sortBtn}
                onClick={() => setSort(dir === 'old' ? 'new' : 'old')}
                title={dir === 'old' ? 'Сначала давние' : 'Сначала новые'}
              >
                {dir === 'old' ? 'Давние ↑' : 'Новые ↑'}
              </button>
            </label>
            <FilterTabs
              value={filter}
              counts={queue.data?.counts}
              onChange={(f) => {
                setFilter(f)
                setSort(null)
                setSelectedId(null)
                setOpened(false)
              }}
            />
          </div>
          {queue.isPending && <div className="spinner" aria-label="Загрузка" />}
          {queue.isError && <p className="error-box">Не удалось загрузить очередь.</p>}
          {queue.data && items.length === 0 && (
            <p className={styles.empty}>{search.trim() ? 'Ничего не найдено. Попробуйте другое имя или тему.' : EMPTY[filter]}</p>
          )}
          {items.length > 0 && (
            <QueueList
              items={items}
              selectedId={selected?.id ?? null}
              now={queue.dataUpdatedAt}
              onSelect={(id) => {
                setSelectedId(id)
                setOpened(true)
              }}
            />
          )}
        </section>
        <section className={styles.chatPane} aria-label="Переписка">
          {selected ? (
            <ChatPane key={selected.id} id={selected.id} onBack={() => setOpened(false)} />
          ) : (
            <p className={styles.empty}>Выберите разбор в списке слева.</p>
          )}
        </section>
      </div>
    </div>
  )
}
